import memoizeOne from 'memoize-one';
import type { ValidationError } from '../../../types/validation.tsx';
import {
	POSITION_ISSUE_TYPE_NAME_INPUT,
	POSITION_ISSUE_TYPE_HELP_TEXT_INPUT,
	POSITION_ISSUE_TYPE_DESCRIPTION_INPUT,
	ISSUE_TYPE_NAME_VALIDATOR,
	ISSUE_TYPE_HELP_TEXT_VALIDATOR,
	ISSUE_TYPE_DESCRIPTION_VALIDATOR,
} from '../constants.tsx';
import {
	MIN_NAME_INPUT_LENGTH,
	MAX_NAME_INPUT_LENGTH,
	MAX_DESCRIPTION_INPUT_LENGTH,
	MAX_HELPTEXT_INPUT_LENGTH,
} from './constants.tsx';
import messages from './messages.tsx';
import type { ValidationMessages, IssueTypeValidators } from './types.tsx';

const nameNormalizer = (str: string) => str.toLocaleLowerCase().trim();

export const validateIssueTypeName = memoizeOne(
	(
		issueTypeName: string,
		existingIssueTypeNames: string[],
		validationMessages: ValidationMessages,
	): ValidationError[] => {
		const normalized = nameNormalizer(issueTypeName);
		const usedNames = existingIssueTypeNames.map(nameNormalizer);
		const errors: Array<ValidationError> = [];
		if (normalized.length < MIN_NAME_INPUT_LENGTH) {
			errors.push({
				position: POSITION_ISSUE_TYPE_NAME_INPUT,
				messageDescriptor:
					validationMessages.errorMessageIssueTypeNameTooShort ??
					messages.errorMessageIssueTypeNameTooShort,
			});
		}

		if (normalized.length > MAX_NAME_INPUT_LENGTH) {
			errors.push({
				position: POSITION_ISSUE_TYPE_NAME_INPUT,
				messageDescriptor:
					validationMessages.errorMessageIssueTypeNameTooLong ??
					messages.errorMessageIssueTypeNameTooLong,
				values: {
					maximumNameLength: MAX_NAME_INPUT_LENGTH,
				},
			});
		}
		if (usedNames.includes(normalized)) {
			errors.push({
				position: POSITION_ISSUE_TYPE_NAME_INPUT,
				messageDescriptor:
					validationMessages.errorMessageIssueTypeNameAlreadyExists ??
					messages.errorMessageIssueTypeNameAlreadyExists,
			});
		}

		return errors;
	},
);

export const validateIssueTypeHelpText = (
	issueTypeHelpText: string,
	validationMessages: ValidationMessages,
) => {
	if (issueTypeHelpText.length > MAX_HELPTEXT_INPUT_LENGTH) {
		return [
			{
				position: POSITION_ISSUE_TYPE_HELP_TEXT_INPUT,
				messageDescriptor:
					validationMessages.errorMessageIssueTypeHelpTextTooLong ??
					messages.errorMessageIssueTypeHelpTextTooLong,
				values: {
					maximumHelpTextLength: MAX_HELPTEXT_INPUT_LENGTH,
				},
			},
		];
	}
	return [];
};

export const validateIssueTypeDescription = memoizeOne(
	(issueTypeDescription: string, validationMessages: ValidationMessages) => {
		if (issueTypeDescription.length > MAX_DESCRIPTION_INPUT_LENGTH) {
			return [
				{
					position: POSITION_ISSUE_TYPE_DESCRIPTION_INPUT,
					messageDescriptor:
						validationMessages.errorMessageIssueTypeDescriptionTooLong ??
						messages.errorMessageDescriptionTooLong,
					values: {
						maximumDescriptionLength: MAX_DESCRIPTION_INPUT_LENGTH,
					},
				},
			];
		}
		return [];
	},
);

const getIssueTypeValidators = memoizeOne((overrideValidators: IssueTypeValidators) => ({
	[ISSUE_TYPE_NAME_VALIDATOR]:
		overrideValidators[ISSUE_TYPE_NAME_VALIDATOR] ?? validateIssueTypeName,
	[ISSUE_TYPE_HELP_TEXT_VALIDATOR]:
		overrideValidators[ISSUE_TYPE_HELP_TEXT_VALIDATOR] ?? validateIssueTypeHelpText,
	[ISSUE_TYPE_DESCRIPTION_VALIDATOR]:
		overrideValidators[ISSUE_TYPE_DESCRIPTION_VALIDATOR] ?? validateIssueTypeDescription,
}));

export const validateIssueType = memoizeOne(
	(
		validationMessages: ValidationMessages,
		overrideValidators: IssueTypeValidators,
		issueTypeName: string | null,
		existingIssueTypeNames: string[],
		issueTypeHelpText: string | null,
		issueTypeDescription: string | null,
	) => {
		const validators = getIssueTypeValidators(overrideValidators);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return Object.keys(validators).reduce<Array<any>>((errors, validatorKey) => {
			switch (validatorKey) {
				case ISSUE_TYPE_NAME_VALIDATOR:
					return [
						// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						...errors,
						...validators[ISSUE_TYPE_NAME_VALIDATOR](
							issueTypeName ?? '',
							existingIssueTypeNames,
							validationMessages,
						),
					];
				case ISSUE_TYPE_HELP_TEXT_VALIDATOR:
					return [
						// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						...errors,
						...validators[ISSUE_TYPE_HELP_TEXT_VALIDATOR](
							issueTypeHelpText ?? '',
							validationMessages,
						),
					];
				case ISSUE_TYPE_DESCRIPTION_VALIDATOR:
					return [
						// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
						...errors,
						...validators[ISSUE_TYPE_DESCRIPTION_VALIDATOR](
							issueTypeDescription ?? '',
							validationMessages,
						),
					];
				default:
					return errors;
			}
		}, []);
	},
);
